/**
 * Performs a binary search to find the index of the datum closest to the right to the given value.
 * @param value - The value to search for.
 * @param datums - The array of data to search over. It is required that the datums are sorted by the value returned by `getDatumValue`.
 * @param getDatumValue - Function to extract the value from a datum.
 * @returns The index of the closest datum to the right of the value.
 */
export function binarySearch<T>(
  value: number,
  datums: T[],
  getDatumValue: (datum: T) => number,
) {
  let leftIndex = 0;
  let rightIndex = datums.length - 1;

  while (leftIndex < rightIndex) {
    const middleIndex = Math.floor((leftIndex + rightIndex) / 2);
    const middleValue = getDatumValue(datums[middleIndex]);

    if (middleValue === value) {
      leftIndex = middleIndex;
      rightIndex = middleIndex;
    } else if (middleValue < value) {
      leftIndex = middleIndex + 1;
    } else {
      rightIndex = middleIndex;
    }
  }

  return leftIndex;
}

/**
 * Finds the boundaries (start and end indices) of the cluster in the datums array that contains the given index. Only the values that share the same cluster value as the datum at the given index will be considered part of the cluster.
 * @param index - The index to start searching from.
 * @param datums - The array of data.
 * @param getClusterValue - Function to extract the cluster value from a datum.
 * @returns The start and end indices of the cluster, or [-1, -1] if index is out of bounds.
 */
export function boundariesOfCluster<T>(
  index: number,
  datums: T[],
  getClusterValue: (datum: T) => number,
) {
  if (index < 0 || index >= datums.length) {
    return [-1, -1];
  }
  const clusterBaseValue = getClusterValue(datums[index]);

  let leftIndex = index;
  let rightIndex = index;

  while (
    leftIndex > 0 &&
    getClusterValue(datums[leftIndex - 1]) === clusterBaseValue
  ) {
    leftIndex--;
  }
  while (
    rightIndex < datums.length - 1 &&
    getClusterValue(datums[rightIndex + 1]) === clusterBaseValue
  ) {
    rightIndex++;
  }

  return [leftIndex, rightIndex] as const;
}

/**
 * Gets the boundaries of the cluster at the position closest to the given value.
 * @param position - The position to search for.
 * @param datums - The array of data.
 * @param getDatumPosition - Function to extract the position from a datum.
 * @param getClusterValue - Function to extract the cluster value from a datum.
 * @returns The start and end indices of the cluster.
 */
export function getClusterBoundariesAtPosition<T>(
  position: number,
  datums: T[],
  getDatumPosition: (datum: T) => number,
  getClusterValue: (datum: T) => number,
) {
  let index = binarySearch(position, datums, getDatumPosition);

  if (index > 0) {
    const datumPosition = getDatumPosition(datums[index]);
    const prevDatumPosition = getDatumPosition(datums[index - 1]);

    if (
      Math.abs(datumPosition - position) >
      Math.abs(prevDatumPosition - position)
    ) {
      index--;
    }
  }

  return boundariesOfCluster(index, datums, getClusterValue);
}

/**
 * Gets the boundaries (indices) of datums within the specified position range.
 * @param positionRange - The start and end positions.
 * @param datums - The array of data.
 * @param getDatumPosition - Function to extract the position from a datum.
 * @returns The start and end indices of the range. If the range is empty, it returns the second index as the first - 1.
 */
export function getClusterBoundariesInRange<T>(
  positionRange: [number, number],
  datums: T[],
  getDatumPosition: (datum: T) => number,
) {
  const [start, end] = positionRange;
  const startIndex = binarySearch(start, datums, getDatumPosition);
  const endIndex = binarySearch(end, datums, getDatumPosition);

  if (startIndex === endIndex) {
    return [-1, -1];
  }

  return boundariesOfCluster(startIndex, datums, (datum) =>
    getDatumPosition(datum) >= start && getDatumPosition(datum) <= end ? 1 : 0,
  );
}
